import * as React from 'react';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {Card} from 'shells/card/card';
import {CalendarSquareRegularActivityIcon} from 'shells/icon/svg/calendar-square-regular-activity-icon';
import {EnvelopeNewIcon} from 'shells/icon/svg/envelope-new-icon';
import {GoogleCalendarIcon} from 'shells/icon/svg/google-calendar-icon';
import {OutlookCalendarIcon} from 'shells/icon/svg/outlook-calendar-icon';
import {Stack} from 'shells/layout/stack/stack';
import {Switch} from 'shells/switch/switch';
import {Body} from 'shells/typography/body';

import type {CalendarSync} from 'nutshell-graphql-types';

import {serviceNameMap} from '../../../setup/calendar/calendar-options';
import {SelectCalendarModalWrapper} from '../../../setup/calendar/select-calendar-modal/select-calendar-modal-wrapper';

const INITIAL_SYNC_NUM_DAYS_AGO = 90;

import styles from './email-calendar-sync-settings-page.module.css';

export type EmailCalendarSyncSettings = {
    syncEmailsFromDaysAgo: number | null;
    importEmailedContacts: boolean;
    enableCalendarExport: boolean;
    enableCalendarImport: boolean;
    createPeopleFromEvents: boolean;
    calendarId: string | null;
};

type Props = {
    onSave: (settings: EmailCalendarSyncSettings) => Promise<void>;
    calendarSync: CalendarSync | null;
    onSaveSuccess: () => void;
};

export function EmailCalendarSyncSettingsPage(props: Props) {
    const [syncEmails, setSyncEmails] = React.useState(true);
    const [importContacts, setImportContacts] = React.useState(true);
    const [calendarExport, setCalendarExport] = React.useState(true);
    const [calendarImport, setCalendarImport] = React.useState(true);
    const [createPeople, setCreatePeople] = React.useState(true);

    const [isChangingCalendar, setIsChangingCalendar] = React.useState(false);

    const [isSaving, setIsSaving] = React.useState(false);

    const calendars = props.calendarSync?.calendars ?? [];
    const defaultCalendarId = calendars[0]?.id ?? null;
    const [selectedCalendarId, setSelectedCalendarId] = React.useState<string | null>(null);

    const activeCalendarId = selectedCalendarId ?? defaultCalendarId;
    const activeCalendar = calendars.find((cal) => cal.id === activeCalendarId);

    const serviceName = props.calendarSync
        ? serviceNameMap[props.calendarSync.externalService] ?? 'Google'
        : 'Google';
    const calendarDisplayName =
        activeCalendar?.name ?? props.calendarSync?.username ?? 'No calendar selected';
    const isOffice365 = props.calendarSync?.externalService === 'OFFICE365';

    const handleSyncEmailsToggle = () => {
        const next = !syncEmails;
        setSyncEmails(next);
        if (!next) {
            setImportContacts(false);
        }
    };

    const handleCalendarImportToggle = () => {
        const next = !calendarImport;
        setCalendarImport(next);
        if (!next) {
            setCreatePeople(false);
        }
    };

    const handleSave = () => {
        setIsSaving(true);
        return props
            .onSave({
                syncEmailsFromDaysAgo: syncEmails ? INITIAL_SYNC_NUM_DAYS_AGO : null,
                importEmailedContacts: importContacts,
                enableCalendarExport: calendarExport,
                enableCalendarImport: calendarImport,
                createPeopleFromEvents: createPeople,
                calendarId: activeCalendarId,
            })
            .then(() => {
                props.onSaveSuccess();
            })
            .catch(() => {
                // Error is surfaced by Apollo's error state
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    return (
        <div className='flex flex-dir-col full-height justify-sb'>
            <div className='pad-32'>
                <Stack spacing={24}>
                    <Body size='xlarge' isBold={true} color='dark'>
                        Configure your email and calendar sync
                    </Body>
                    <Card hasBorder={true} borderRadius={8}>
                        <div className={styles['section-header']}>
                            <EnvelopeNewIcon size={20} alignCenter={true} />
                            <Body color='dark'>Email</Body>
                        </div>
                        <div className='pad-16'>
                            <Stack spacing={16}>
                                <Stack isHorizontal={true} spacing={12} alignItems='center'>
                                    <Switch
                                        isOn={syncEmails}
                                        onChange={handleSyncEmailsToggle}
                                        isRounded={true}
                                        isSmall={true}
                                    />
                                    <Body>
                                        Sync emails from the last {INITIAL_SYNC_NUM_DAYS_AGO} days
                                    </Body>
                                </Stack>
                                <Stack isHorizontal={true} spacing={12} alignItems='center'>
                                    <Switch
                                        isOn={importContacts}
                                        onChange={() => setImportContacts(!importContacts)}
                                        isDisabled={!syncEmails}
                                        isRounded={true}
                                        isSmall={true}
                                    />
                                    <Body>
                                        Import people I've emailed in the past{' '}
                                        {INITIAL_SYNC_NUM_DAYS_AGO} days
                                    </Body>
                                </Stack>
                            </Stack>
                        </div>
                    </Card>
                    <Card hasBorder={true} borderRadius={8}>
                        <div className={styles['section-header']}>
                            <CalendarSquareRegularActivityIcon size={20} alignCenter={true} />
                            <Body color='dark'>Calendar</Body>
                        </div>
                        <div className='pad-16'>
                            <Stack spacing={16}>
                                {props.calendarSync && (
                                    <>
                                        <Stack
                                            isHorizontal={true}
                                            alignItems='center'
                                            justifyContent='space-between'
                                        >
                                            <div className='flex align-center gap-8'>
                                                {isOffice365 ? (
                                                    <OutlookCalendarIcon size={24} />
                                                ) : (
                                                    <GoogleCalendarIcon size={24} />
                                                )}
                                                <Body>{calendarDisplayName}</Body>
                                            </div>
                                            {calendars.length > 1 && (
                                                <Button
                                                    variant='text-primary'
                                                    size='xlarge'
                                                    onClick={() => setIsChangingCalendar(true)}
                                                >
                                                    Change calendar
                                                </Button>
                                            )}
                                        </Stack>
                                        {isChangingCalendar && (
                                            <SelectCalendarModalWrapper
                                                username={props.calendarSync.username}
                                                isOpen={isChangingCalendar}
                                                onClose={() => setIsChangingCalendar(false)}
                                                onSave={(calendarId) => {
                                                    setSelectedCalendarId(calendarId);
                                                    setIsChangingCalendar(false);

                                                    return Promise.resolve();
                                                }}
                                                modalType='sync'
                                            />
                                        )}
                                    </>
                                )}
                                <Stack isHorizontal={true} spacing={12} alignItems='center'>
                                    <Switch
                                        isOn={calendarExport}
                                        onChange={() => setCalendarExport(!calendarExport)}
                                        isRounded={true}
                                        isSmall={true}
                                    />
                                    <Body>
                                        Add my Nutshell activities to my {serviceName} calendar
                                    </Body>
                                </Stack>
                                <Stack isHorizontal={true} spacing={12} alignItems='center'>
                                    <Switch
                                        isOn={calendarImport}
                                        onChange={handleCalendarImportToggle}
                                        isRounded={true}
                                        isSmall={true}
                                    />
                                    <Body>
                                        Create Nutshell activities from {serviceName} calendar
                                        events
                                    </Body>
                                </Stack>
                                <Stack isHorizontal={true} spacing={12} alignItems='center'>
                                    <Switch
                                        isOn={createPeople}
                                        onChange={() => setCreatePeople(!createPeople)}
                                        isDisabled={!calendarImport}
                                        isRounded={true}
                                        isSmall={true}
                                    />
                                    <Body>
                                        Create people in Nutshell from {serviceName} calendar events
                                    </Body>
                                </Stack>
                            </Stack>
                        </div>
                    </Card>
                </Stack>
            </div>
            <div className='flex justify-end pad-32'>
                <SaveButton
                    variant='primary'
                    size='big'
                    onClick={handleSave}
                    disabled={!activeCalendarId}
                    isSaving={isSaving}
                    buttonText='Save'
                    savingText='Saving...'
                    successText='Saved!'
                />
            </div>
        </div>
    );
}
